import request, { adminForm } from '@/api/request'
import type { SpringPage } from '@/api/types/domain'

/** `GET …/daikuan/loans` 返回，含 userinfo / 钱包快照与英文列表态展示 */
export interface DaikuanLoanRow {
  id: number
  orderNum?: string | null
  user?: string | null
  customerName?: string | null
  money?: number | null
  months?: number | null
  monthmoney?: number | null
  addtime?: number | null
  status?: number | null
  statusText?: string | null
  text?: string | null
  bank?: string | null
  banknum?: string | null
  orderAddMoney?: number | null
  walletBalance?: number | null
  discode?: number | null
  yzpzImg?: string | null
  forwardStatus?: number | null
  forward?: string | null
  statusDisplayEn?: string | null
}

export function fetchDaikuanLoans(params: {
  /** 手机号或订单号，同一关键词模糊匹配（任一命中） — 与普通筛选互斥优先级见后端文档 */
  phoneOrOrder?: string
  keywordUser?: string
  orderNumExact?: string
  /** Fuzzy match on profile real name (`userinfo.name`), same phone as order user */
  keywordName?: string
  page?: number
  rows?: number
}) {
  return request.get<SpringPage<DaikuanLoanRow>>('/api/admin/backoffice/daikuan/loans', {
    params,
  })
}

export function deleteDaikuanLoan(id: number) {
  return request.delete<{ status: number; msg: string }>(`/api/admin/backoffice/daikuan/loans/${id}`)
}

export function fetchDaikuanRecycleLoans(params: { page?: number; rows?: number }) {
  return request.get<SpringPage<DaikuanLoanRow>>('/api/admin/backoffice/daikuan/loans/recycle', {
    params,
  })
}

export function restoreDaikuanLoan(id: number) {
  return request.post<{ status: number; msg: string }>(
    `/api/admin/backoffice/daikuan/loans/${id}/restore`,
  )
}

export function fetchDaikuanContract(loanId: number) {
  return request.get<{ html: string }>(`/api/admin/backoffice/daikuan/contract/${loanId}`)
}

const formHeaders = { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' }

export function daikuanChangeStatus(data: {
  id: number
  status: number
  statusText?: string
  smsContent?: string
  /** 对标旧版 Prompt information，入库 `order.text` */
  text?: string
  forceSmsbao?: boolean
}) {
  return request.post<{ status?: number; msg?: string }>(
    '/api/admin/backoffice/daikuan/loans/status',
    adminForm({
      id: data.id,
      status: data.status,
      ...(data.statusText != null ? { statusText: data.statusText } : {}),
      ...(data.smsContent != null ? { smsContent: data.smsContent } : {}),
      ...(data.text !== undefined ? { text: data.text } : {}),
      forceSmsbao: data.forceSmsbao ?? false,
    }),
    { headers: formHeaders },
  )
}

/** 审核通过后：把订单本金一次性写入 borrower 钱包（覆盖 user.money，非追加） */
export function daikuanSyncPrincipalWallet(loanId: number) {
  const params = new URLSearchParams()
  params.set('loanId', String(loanId))
  return request.post<{ status: number; msg: string }>(
    `/api/admin/backoffice/daikuan/loans/sync-principal-wallet?${params.toString()}`,
  )
}

export function daikuanAddMoney(loanId: number, money: number) {
  return request.post<{ status?: number; msg?: string }>(
    '/api/admin/backoffice/daikuan/loans/add-money',
    adminForm({ loanId, money }),
    { headers: formHeaders },
  )
}

/** 对标 savemoney — 校验 site 配置下的金额区间与允许的期数档位 */
export function daikuanSaveMoneyMonths(loanId: number, money: number, months: number) {
  return request.post<{ status?: number; msg?: string }>(
    '/api/admin/backoffice/daikuan/loans/save-money-months',
    adminForm({ loanId, money, months }),
    { headers: formHeaders },
  )
}

/** 对标 savebanks：可传订单用户手机号或数值 loanId（后端 resolve） */
export function daikuanSaveBanks(userOrLoanId: string, bank: string, banknum: string) {
  return request.post<{ status?: number; msg?: string }>(
    '/api/admin/backoffice/daikuan/users/banks',
    adminForm({ userOrLoanId, bank, banknum }),
    { headers: formHeaders },
  )
}

/** 服务端 Smsbao，不向浏览器暴露网关账号口令 */
export function daikuanSendCustomSms(phone: string, content: string) {
  return request.post(
    '/api/admin/backoffice/daikuan/loans/send-custom-sms',
    adminForm({ phone, content }),
    { headers: formHeaders },
  )
}

export function daikuanSetWithdrawalCode(loanId: number, code: string) {
  return request.patch<{ status?: number; msg?: string; discode?: number }>(
    `/api/admin/backoffice/daikuan/loans/${loanId}/withdrawal-code`,
    { code },
  )
}

export function daikuanSendWithdrawalSms(loanId: number) {
  return request.post<{ status?: number; msg?: string }>(
    `/api/admin/backoffice/daikuan/loans/${loanId}/send-withdrawal-sms`,
  )
}
